import React, { useState, useEffect } from 'react';
import { Database, Plus, Trash2, Settings, Zap } from 'lucide-react';

const CONFIG_API = `/api/config`;

const MasterConfig = () => {
  const [tanks, setTanks] = useState([]);
  const [pumps, setPumps] = useState([]);
  
  // New Tank State
  const [newTank, setNewTank] = useState({ tank_name: '', fuel_type: 'Petrol', capacity: '' });
  
  // New Pump State
  const [newPump, setNewPump] = useState({ pump_name: '', nozzles: [{ nozzle_number: 1, fuel_type: 'Petrol', tank_id: '' }] });

  const isAdmin = localStorage.getItem('role') === 'admin';

  const fetchConfig = () => {
    fetch(CONFIG_API, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    })
      .then(res => res.json())
      .then(data => {
        setTanks(data.tanks || []);
        setPumps(data.pumps || []);
      })
      .catch(err => console.error("Error fetching config:", err));
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const handleAddTank = (e) => {
    e.preventDefault();
    if (!newTank.tank_name || !newTank.capacity) return alert("Fill all fields");

    fetch(CONFIG_API, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({ ...newTank, type: 'tank' })
    })
    .then(res => res.json())
    .then(data => {
       if (data.status === 'success') {
         setNewTank({ tank_name: '', fuel_type: 'Petrol', capacity: '' });
         fetchConfig();
       } else alert(data.error);
    });
  };

  const handleDelete = (type, id) => {
    if (!window.confirm(`Are you sure you want to delete this ${type}?`)) return;
    
    fetch(CONFIG_API, {
      method: 'DELETE',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({ type, id })
    })
    .then(res => res.json())
    .then(data => {
       if (data.status === 'success') fetchConfig();
       else alert(data.error);
    });
  };

  const handleAddNozzleRow = () => {
    setNewPump({
      ...newPump,
      nozzles: [
        ...newPump.nozzles,
        { nozzle_number: newPump.nozzles.length + 1, fuel_type: 'Petrol', tank_id: '' }
      ]
    });
  };

  const handleNozzleChange = (index, field, value) => {
    const updatedNozzles = [...newPump.nozzles];
    updatedNozzles[index][field] = value;
    if (field === 'fuel_type') updatedNozzles[index].tank_id = ''; // tank options change with fuel type
    setNewPump({ ...newPump, nozzles: updatedNozzles });
  };

  const handleRemoveNozzleRow = (index) => {
    const updatedNozzles = [...newPump.nozzles];
    updatedNozzles.splice(index, 1);
    // Renumber
    updatedNozzles.forEach((n, i) => n.nozzle_number = i + 1);
    setNewPump({ ...newPump, nozzles: updatedNozzles });
  };

  const handleAddPump = (e) => {
    e.preventDefault();
    if (!newPump.pump_name || newPump.nozzles.length === 0) return alert("Fill pump name and add at least 1 nozzle");
    for (const noz of newPump.nozzles) {
      const matchingTanks = tanks.filter(t => t.fuel_type === noz.fuel_type);
      if (matchingTanks.length > 1 && !noz.tank_id) {
        return alert(`Nozzle ${noz.nozzle_number}: please select which ${noz.fuel_type} tank it's connected to.`);
      }
    }

    fetch(CONFIG_API, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({ ...newPump, type: 'pump' })
    })
    .then(res => res.json())
    .then(data => {
       if (data.status === 'success') {
         setNewPump({ pump_name: '', nozzles: [{ nozzle_number: 1, fuel_type: 'Petrol' }] });
         fetchConfig();
       } else alert(data.error);
    });
  };

  return (
    <div className="space-y-8 max-w-6xl mx-auto pb-12">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Master Configuration</h1>
        <p className="text-slate-500 text-sm mt-1">Dynamically manage your underground tanks and physical pumps.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* TANKS CONFIGURATION */}
        <div className="space-y-6">
          {isAdmin && (
            <div className="glass-panel p-6">
              <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2 mb-4">
              <Database className="w-5 h-5 text-indigo-500" /> Add Underground Tank
            </h2>
            <form onSubmit={handleAddTank} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Tank Name / Code</label>
                <input 
                  type="text" 
                  className="input-field" 
                  placeholder="e.g. Tank 1"
                  value={newTank.tank_name}
                  onChange={e => setNewTank({...newTank, tank_name: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Fuel Type</label>
                  <select 
                    className="input-field"
                    value={newTank.fuel_type}
                    onChange={e => setNewTank({...newTank, fuel_type: e.target.value})}
                  >
                    <option value="Petrol">Petrol (MS)</option>
                    <option value="Diesel">Diesel (HSD)</option>
                    <option value="Xtragreen">Xtragreen (XG)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Capacity (Liters)</label>
                  <input 
                    type="number" 
                    className="input-field" 
                    placeholder="20000"
                    value={newTank.capacity}
                    onChange={e => setNewTank({...newTank, capacity: e.target.value})}
                  />
                </div>
              </div>
              <button type="submit" className="w-full btn-primary flex justify-center items-center gap-2">
                <Plus className="w-4 h-4" /> Add Tank
              </button>
            </form>
          </div>
          )}

          <div className="glass-panel overflow-hidden">
             <div className="bg-slate-50 border-b border-slate-100 p-4 font-bold text-slate-800">Existing Tanks</div>
             <div className="p-4 space-y-3">
               {tanks.length === 0 && <p className="text-sm text-slate-500 text-center py-4">No tanks configured.</p>}
               {tanks.map(tank => (
                 <div key={tank.id} className="flex justify-between items-center p-3 border border-slate-200 rounded-xl bg-white shadow-sm">
                    <div>
                      <h4 className="font-bold text-slate-800">{tank.tank_name}</h4>
                      <p className="text-xs text-slate-500">{tank.fuel_type} • {parseFloat(tank.capacity).toLocaleString()} L</p>
                    </div>
                    {isAdmin && (
                      <button onClick={() => handleDelete('tank', tank.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                 </div>
               ))}
             </div>
          </div>
        </div>

        {/* PUMPS CONFIGURATION */}
        <div className="space-y-6">
          {isAdmin && (
            <div className="glass-panel p-6">
              <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2 mb-4">
              <Zap className="w-5 h-5 text-amber-500" /> Add Dispensing Pump
            </h2>
            <form onSubmit={handleAddPump} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Pump Name / ID</label>
                <input 
                  type="text" 
                  className="input-field" 
                  placeholder="e.g. Pump 1"
                  value={newPump.pump_name}
                  onChange={e => setNewPump({...newPump, pump_name: e.target.value})}
                />
              </div>
              
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
                <div className="flex justify-between items-center">
                  <label className="block text-xs font-bold text-slate-700">Attach Nozzles</label>
                  <button type="button" onClick={handleAddNozzleRow} className="text-xs font-bold text-indigo-600 hover:text-indigo-800">+ Add Nozzle</button>
                </div>
                {newPump.nozzles.map((noz, idx) => {
                  const matchingTanks = tanks.filter(t => t.fuel_type === noz.fuel_type);
                  return (
                  <div key={idx} className="flex gap-2 items-center">
                    <span className="text-sm font-semibold text-slate-500 w-20">Nozzle {noz.nozzle_number}</span>
                    <select
                      className="input-field flex-1 py-1.5 px-3"
                      value={noz.fuel_type}
                      onChange={e => handleNozzleChange(idx, 'fuel_type', e.target.value)}
                    >
                      <option value="Petrol">Petrol</option>
                      <option value="Diesel">Diesel</option>
                      <option value="Xtragreen">Xtragreen</option>
                    </select>
                    {matchingTanks.length > 1 && (
                      <select
                        className="input-field flex-1 py-1.5 px-3"
                        value={noz.tank_id}
                        onChange={e => handleNozzleChange(idx, 'tank_id', e.target.value)}
                      >
                        <option value="">Which tank?</option>
                        {matchingTanks.map(t => (
                          <option key={t.id} value={t.id}>{t.tank_name}</option>
                        ))}
                      </select>
                    )}
                    {newPump.nozzles.length > 1 && (
                      <button type="button" onClick={() => handleRemoveNozzleRow(idx)} className="p-1 text-slate-400 hover:text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  );
                })}
              </div>

              <button type="submit" className="w-full btn-primary flex justify-center items-center gap-2">
                <Plus className="w-4 h-4" /> Add Pump
              </button>
            </form>
          </div>
          )}

          <div className="glass-panel overflow-hidden">
             <div className="bg-slate-50 border-b border-slate-100 p-4 font-bold text-slate-800">Existing Pumps</div>
             <div className="p-4 space-y-3">
               {pumps.length === 0 && <p className="text-sm text-slate-500 text-center py-4">No pumps configured.</p>}
               {pumps.map(pump => (
                 <div key={pump.id} className="p-4 border border-slate-200 rounded-xl bg-white shadow-sm">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-bold text-slate-800">{pump.pump_name}</h4>
                      {isAdmin && (
                        <button onClick={() => handleDelete('pump', pump.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {pump.nozzles.map(n => {
                        const tank = tanks.find(t => t.id === n.tank_id);
                        return (
                        <span key={n.id} className={`text-xs font-semibold px-2 py-1 rounded border ${
                          n.fuel_type === 'Petrol' ? 'bg-orange-50 text-orange-700 border-orange-200' :
                          n.fuel_type === 'Diesel' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                          'bg-emerald-50 text-emerald-700 border-emerald-200'
                        }`}>
                          N{n.nozzle_number}: {n.fuel_type}{tank ? ` (${tank.tank_name})` : ''}
                        </span>
                        );
                      })}
                    </div>
                 </div>
               ))}
             </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default MasterConfig;
